import {
  useState, useEffect, axios, AnimatePresence, Link,
  Button, Input, Card, CardContent, CardHeader, CardTitle, Badge, Label,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  Loader2, Sparkles, Copy, Trash2, Eye, Calendar, Clock, Check, X,
  Twitter, Linkedin, Facebook, MessageCircle, ChevronDown, Zap,
  Instagram, Video, AlertTriangle, Mail,
  useSearchParams, API, useAuth, toast
} from "@/pages/shared";
import PodcastClipReels from "../../components/PodcastClipReels";
import { detectBlockedTerms, suggestRewrite } from "../../lib/sora2Moderation";
import { buildViralSignature, makeShareHandlers } from "../../components/content/shareUtils";
import { Globe, EyeOff } from "lucide-react";
import { ScheduleControls } from "../../components/content/ScheduleControls";
import { ContentViewModal } from "../../components/content/ContentViewModal";
import { LimitReachedModal } from "../../components/content/LimitReachedModal";

export default function ContentPage() {
  const { token, user } = useAuth();
  const [searchParams] = useSearchParams();
  const [contentType, setContentType] = useState("blog");
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("professional");
  const [length, setLength] = useState("medium");
  const [templateId, setTemplateId] = useState("none");
  const [generating, setGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState("");
  const [lastGenerated, setLastGenerated] = useState(null); // full content doc from latest /generate response — used to surface clip reels inline
  const [contentList, setContentList] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [schedulingId, setSchedulingId] = useState("");
  const [viewingContent, setViewingContent] = useState(null);
  const [seoTemplateName, setSeoTemplateName] = useState(null);
  const [seoTemplateSlug, setSeoTemplateSlug] = useState("");
  const [seoTemplates, setSeoTemplates] = useState([]);
  const [showFeatured, setShowFeatured] = useState(true);
  const [branding, setBranding] = useState(null);
  const [limitModalOpen, setLimitModalOpen] = useState(false);
  const [linkedinConn, setLinkedinConn] = useState(null); // {connected, name, email, picture} — drives "Posts as @X" hint
  const [twitterConn, setTwitterConn] = useState(null); // {connected, screen_name} — drives "Posts as @handle" hint

  useEffect(() => { fetchContent(); fetchTemplates(); }, [token]);

  // Fetch personal LinkedIn/X OAuth status so the schedule modal can show
  // "Posts as @Name" instead of leaving users guessing.
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/social-connections/linkedin/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setLinkedinConn(r.data))
      .catch(() => setLinkedinConn({ connected: false }));
    axios.get(`${API}/social-connections/twitter/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setTwitterConn(r.data))
      .catch(() => setTwitterConn({ connected: false }));
  }, [token]);

  // Fetch the user's branding (signature + powered-by toggle) so share captions
  // honor white-label settings for paid users.
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/branding`).then((r) => setBranding(r.data)).catch(() => {});
  }, [token]);

  // Fetch featured SEO templates
  useEffect(() => {
    axios.get(`${API}/seo/templates`).then(res => setSeoTemplates(res.data)).catch(() => {});
  }, []);

  // Pre-load SEO template settings if arriving from /t/ page
  useEffect(() => {
    const seoSlug = searchParams.get("template");
    if (seoSlug) {
      axios.get(`${API}/seo/templates/${seoSlug}`).then(res => {
        const t = res.data;
        setContentType(t.content_type || "blog");
        setTone(t.tone || "professional");
        setLength(t.length || "medium");
        setSeoTemplateName(t.name);
        setSeoTemplateSlug(t.slug);
        toast.success(`Template "${t.name}" loaded! Enter your topic and click Generate.`);
      }).catch(() => {});
    }
  }, [searchParams]);

  const fetchContent = async () => { try { const res = await axios.get(`${API}/content`, { headers: { Authorization: `Bearer ${token}` } }); setContentList(res.data); } catch (e) {} setLoadingList(false); };
  const fetchTemplates = async () => { try { const res = await axios.get(`${API}/templates`, { headers: { Authorization: `Bearer ${token}` } }); setTemplates(res.data); } catch (e) {} };

  const getViralSignature = () => buildViralSignature(user, branding);
  const share = makeShareHandlers(getViralSignature);

  const publishDemo = async (c) => {
    try {
      if (c.demo_slug) {
        await navigator.clipboard.writeText(`${window.location.origin}/d/${c.demo_slug}`).catch(() => {});
        toast.success("Public link copied!");
        return;
      }
      const res = await axios.post(`${API}/demos/publish`, { content_id: c.id }, { headers: { Authorization: `Bearer ${token}` } });
      await navigator.clipboard.writeText(`${window.location.origin}/d/${res.data.slug}`).catch(() => {});
      toast.success("Public page created — link copied! Anyone who signs up from it earns you referral credit.");
      fetchContent();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to publish"); }
  };

  const unpublishDemo = async (c) => {
    try {
      await axios.delete(`${API}/demos/${c.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Public page removed");
      fetchContent();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to unpublish"); }
  };

  // When user picks a saved template, auto-fill the form with the template's saved defaults.
  // The user can still override any of these before clicking Generate.
  const handleTemplateChange = (id) => {
    setTemplateId(id);
    if (id && id !== "none") {
      const t = templates.find((x) => x.id === id);
      if (t) {
        if (t.content_type) setContentType(t.content_type);
        if (t.tone) setTone(t.tone);
        if (t.length) setLength(t.length);
      }
    }
  };

  const generateContent = async () => {
    if (!topic.trim()) { toast.error("Please enter a topic"); return; }
    setGenerating(true); setGeneratedContent("");
    try {
      const res = await axios.post(`${API}/content/generate`, { content_type: contentType, topic, tone, length, template_id: templateId && templateId !== "none" ? templateId : null, seo_template_slug: seoTemplateSlug || null }, { headers: { Authorization: `Bearer ${token}` } });
      setGeneratedContent(res.data.generated_content);
      setLastGenerated(res.data);
      toast.success("Content generated!"); fetchContent();
    } catch (e) {
      const detail = e.response?.data?.detail || "Failed to generate";
      // Surface the dedicated limit-reached upsell modal instead of a plain toast
      if (typeof detail === "string" && detail.toLowerCase().includes("content limit reached")) {
        setLimitModalOpen(true);
      } else {
        toast.error(detail);
      }
    }
    setGenerating(false);
  };

  const scheduleContent = async (contentId, opts) => {
    if (!opts.scheduleDate) { toast.error("Select a date"); return; }
    try {
      // scheduleDate is from <input type="datetime-local"> → naive local time.
      // Convert to ISO/UTC so the backend's UTC scheduler fires at the right moment.
      const scheduled_at = new Date(opts.scheduleDate).toISOString();
      await axios.post(
        `${API}/content/schedule`,
        {
          content_id: contentId,
          scheduled_at,
          post_to_twitter: opts.autoPostX,
          post_to_linkedin: opts.autoPostLI,
          post_to_facebook: opts.autoPostFB,
          post_to_instagram: opts.autoPostIG,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const tzLabel = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const platforms = [opts.autoPostX && "X", opts.autoPostLI && "LinkedIn", opts.autoPostFB && "Facebook", opts.autoPostIG && "Instagram"].filter(Boolean);
      toast.success(platforms.length ? `Will auto-post to ${platforms.join(" + ")} (${tzLabel})` : `Email reminder set — we'll ping you at this time (${tzLabel})`);
      setSchedulingId(""); fetchContent();
    } catch (e) { toast.error("Failed"); }
  };

  const unscheduleContent = async (contentId) => {
    // Snapshot the prior schedule from the current list so we can offer one-click Undo
    const prior = contentList.find((c) => c.id === contentId);
    const priorScheduledAt = prior?.scheduled_at;
    const priorPostToTwitter = prior?.post_to_twitter;
    try {
      await axios.post(`${API}/content/${contentId}/unschedule`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (viewingContent?.id === contentId) setViewingContent({ ...viewingContent, status: "draft", scheduled_at: null });
      fetchContent();
      toast.success("Schedule cancelled", {
        action: priorScheduledAt ? {
          label: "Undo",
          onClick: async () => {
            try {
              await axios.post(
                `${API}/content/schedule`,
                { content_id: contentId, scheduled_at: priorScheduledAt, post_to_twitter: !!priorPostToTwitter },
                { headers: { Authorization: `Bearer ${token}` } }
              );
              toast.success("Schedule restored");
              fetchContent();
            } catch (e) { toast.error("Couldn't restore schedule"); }
          },
        } : undefined,
        duration: 6000,
      });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to cancel"); }
  };

  const deleteContent = async (id) => { try { await axios.delete(`${API}/content/${id}`, { headers: { Authorization: `Bearer ${token}` } }); toast.success("Deleted"); fetchContent(); } catch (e) { toast.error("Failed"); } };

  const applySeoTemplate = (t) => {
    setContentType(t.content_type || "blog");
    setTone(t.tone || "professional");
    setLength(t.length || "medium");
    setSeoTemplateName(t.name);
    setSeoTemplateSlug(t.slug);
    setShowFeatured(false);
    toast.success(`Template "${t.name}" loaded!`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="space-y-8">
      <div><h1 className="text-3xl font-bold mb-2">AI Content Generator</h1></div>
      {seoTemplateName && (
        <div className="flex items-center gap-3 p-4 rounded-xl bg-violet-600/10 border border-violet-600/20" data-testid="seo-template-banner">
          <Sparkles className="w-5 h-5 text-violet-500 shrink-0" />
          <div className="flex-1">
            <p className="font-medium text-sm">Template: <span className="text-violet-400">{seoTemplateName}</span></p>
            <p className="text-xs text-muted-foreground">Settings pre-loaded. Enter your topic below and click Generate.</p>
          </div>
          <Button variant="ghost" size="sm" className="shrink-0 text-xs" onClick={() => { setSeoTemplateName(null); setSeoTemplateSlug(""); }}><X className="w-3 h-3" /></Button>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="glass">
          <CardHeader><CardTitle>Generate Content</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {templates.length > 0 && (
              <div><Label>Use Template (Optional)</Label><Select value={templateId} onValueChange={handleTemplateChange}><SelectTrigger className="mt-1 bg-white/5 border-white/10"><SelectValue placeholder="Select a template..." /></SelectTrigger><SelectContent><SelectItem value="none">No template</SelectItem>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select></div>
            )}
            <div><Label>Content Type</Label><Select value={contentType} onValueChange={setContentType}><SelectTrigger className="mt-1 bg-white/5 border-white/10"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="blog">Blog Post</SelectItem><SelectItem value="social">Social Media</SelectItem><SelectItem value="ad_copy">Ad Copy</SelectItem><SelectItem value="twitter">Twitter Thread</SelectItem><SelectItem value="instagram">Instagram Caption</SelectItem><SelectItem value="tiktok">TikTok Script</SelectItem></SelectContent></Select></div>
            <div>
              <Label>Topic</Label>
              <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g., Benefits of remote work" className="mt-1 bg-white/5 border-white/10" data-testid="topic-input" />
              {(() => {
                const hits = detectBlockedTerms(topic);
                if (hits.length === 0) return null;
                const rewrite = suggestRewrite(topic, hits);
                return (
                  <div className="mt-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30" data-testid="blocked-terms-warning">
                    <div className="flex items-start gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                      <div className="text-xs leading-relaxed">
                        <p className="text-amber-300 font-medium mb-1">Sora 2 will likely block this video prompt</p>
                        <p className="text-muted-foreground">
                          Detected: <span className="text-amber-300 font-mono">{hits.map(h => h.term).join(", ")}</span>. Sora 2 refuses real brand / person / sensitive terms. Text generation still works, but any Reel render will fail.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-6">
                      <button
                        type="button"
                        onClick={() => setTopic(rewrite)}
                        className="text-xs px-2.5 py-1 rounded-md bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 font-medium transition-colors"
                        data-testid="blocked-terms-rewrite-btn"
                      >
                        Use suggested rewrite →
                      </button>
                      <span className="text-xs text-muted-foreground italic truncate" title={rewrite}>"{rewrite.slice(0, 80)}{rewrite.length > 80 ? "…" : ""}"</span>
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Tone</Label><Select value={tone} onValueChange={setTone}><SelectTrigger className="mt-1 bg-white/5 border-white/10"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="professional">Professional</SelectItem><SelectItem value="casual">Casual</SelectItem><SelectItem value="humorous">Humorous</SelectItem></SelectContent></Select></div>
              <div><Label>Length</Label><Select value={length} onValueChange={setLength}><SelectTrigger className="mt-1 bg-white/5 border-white/10"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="short">Short</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="long">Long</SelectItem></SelectContent></Select></div>
            </div>
            <Button onClick={generateContent} disabled={generating} className="w-full btn-primary-glow bg-violet-600 hover:bg-violet-700 rounded-full">{generating ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Generating...</> : <><Sparkles className="w-4 h-4 mr-2" />Generate</>}</Button>
          </CardContent>
        </Card>
        <Card className="glass">
          <CardHeader className="flex flex-row items-center justify-between"><CardTitle>Generated Content</CardTitle>{generatedContent && <Button variant="ghost" size="icon" onClick={() => { share.copyWithSignature(generatedContent); }}><Copy className="w-4 h-4" /></Button>}</CardHeader>
          <CardContent>{generatedContent ? <div className="whitespace-pre-wrap text-sm max-h-80 overflow-auto">{generatedContent}</div> : <p className="text-muted-foreground text-center py-12">Your content will appear here</p>}</CardContent>
          {lastGenerated && generatedContent && /\bClip\s*\d+\s*[—–-]/i.test(generatedContent) && (
            <PodcastClipReels content={lastGenerated} userPlan={user?.plan} token={token} />
          )}
        </Card>
      </div>

      {/* Featured Revenue Templates */}
      {seoTemplates.length > 0 && (
        <Card className="glass border-violet-500/10" data-testid="featured-templates-section">
          <CardHeader className="cursor-pointer" onClick={() => setShowFeatured(!showFeatured)}>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2"><Zap className="w-5 h-5 text-amber-500" /> Featured Templates · Revenue-optimised</CardTitle>
              <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${showFeatured ? 'rotate-180' : ''}`} />
            </div>
            <p className="text-sm text-muted-foreground">8 pre-built templates including Podcast Repurposing (with one-click Sora 2 reels), Affiliate Reviews, Lead Magnets and more. Click any to load.</p>
          </CardHeader>
          {showFeatured && (
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {seoTemplates.map((t) => (
                  <button
                    key={t.slug}
                    onClick={() => applySeoTemplate(t)}
                    className={`text-left p-3 rounded-lg border transition-all hover:bg-white/10 ${
                      seoTemplateName === t.name ? 'border-violet-500 bg-violet-500/10' : 'border-white/10 bg-white/5'
                    }`}
                    data-testid={`featured-template-${t.slug}`}
                  >
                    <p className="font-medium text-sm mb-1">{t.name}</p>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-xs">{t.content_type}</Badge>
                      <Badge variant="outline" className="text-xs">{t.tone}</Badge>
                    </div>
                  </button>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      <Card className="glass">
        <CardHeader><CardTitle>Content History</CardTitle></CardHeader>
        <CardContent>
          {loadingList ? <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div> : contentList.length === 0 ? <p className="text-muted-foreground text-center py-8">No content yet</p> : (
            <div className="space-y-4">
              {contentList.slice(0, 10).map((c) => (
                <div key={c.id} className="flex items-start justify-between p-4 rounded-lg bg-white/5">
                  <div className="flex-1 min-w-0 mr-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="secondary">{c.content_type}</Badge>
                      {c.status === 'scheduled' && (() => {
                        const targets = [c.post_to_twitter && "X", c.post_to_linkedin && "LI", c.post_to_facebook && "FB", c.post_to_instagram && "IG"].filter(Boolean);
                        const targetLabel = targets.length ? `Auto-post: ${targets.join("+")}` : "Email reminder";
                        return (
                          <Badge className={targets.length ? "bg-teal-500/20 text-teal-500" : "bg-blue-500/20 text-blue-300"} title={c.scheduled_at ? new Date(c.scheduled_at).toLocaleString() : "Scheduled"}>
                            <Clock className="w-3 h-3 mr-1" />
                            {c.scheduled_at ? new Date(c.scheduled_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "Scheduled"}
                            {" · "}{targetLabel}
                          </Badge>
                        );
                      })()}
                      {c.status === 'reminded' && (
                        <Badge className="bg-blue-500/20 text-blue-300" title={c.reminder_sent_at ? `Reminder emailed ${new Date(c.reminder_sent_at).toLocaleString()}` : ""} data-testid={`reminded-badge-${c.id}`}>
                          <Mail className="w-3 h-3 mr-1" />Reminder sent
                        </Badge>
                      )}
                      {c.status === 'posted' && (
                        <div className="flex gap-1" data-testid={`posted-channels-${c.id}`}>
                          {c.twitter_posted && <Badge className="bg-blue-500/20 text-blue-400 text-[10px]" title="Posted to X"><Twitter className="w-3 h-3" /></Badge>}
                          {c.linkedin_posted && <Badge className="bg-blue-600/20 text-blue-300 text-[10px]" title={c.linkedin_post_url || "Posted to LinkedIn"}><Linkedin className="w-3 h-3" /></Badge>}
                          {c.facebook_posted && <Badge className="bg-blue-500/20 text-blue-300 text-[10px]" title={c.facebook_post_url || "Posted to Facebook"}><Facebook className="w-3 h-3" /></Badge>}
                          {c.instagram_posted && <Badge className="bg-pink-500/20 text-pink-300 text-[10px]" title="Posted to Instagram"><Instagram className="w-3 h-3" /></Badge>}
                        </div>
                      )}
                      {c.status === 'scheduled' && !c.twitter_posted && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-[10px] text-amber-400 hover:text-amber-300 hover:bg-amber-400/10"
                          onClick={() => unscheduleContent(c.id)}
                          title="Cancel this scheduled post (keeps the content as a draft)"
                          data-testid={`unschedule-btn-${c.id}`}
                        >
                          <X className="w-3 h-3 mr-0.5" />Cancel
                        </Button>
                      )}
                      {c.image_url && <Badge className="bg-purple-500/20 text-purple-400">Has Image</Badge>}
                      {c.demo_slug && <Badge className="bg-amber-500/20 text-amber-400" data-testid={`public-badge-${c.id}`}>Public</Badge>}
                      {typeof c.monetization_score === 'number' && (
                        <Badge
                          className={c.monetization_score >= 75 ? "bg-teal-500/20 text-teal-400" : c.monetization_score >= 55 ? "bg-amber-500/20 text-amber-400" : "bg-violet-500/20 text-violet-400"}
                          title="AI-predicted revenue potential — based on CTAs, urgency, benefits, length, and hashtags"
                          data-testid={`monetization-score-${c.id}`}
                        >
                          💰 {c.monetization_score}/100
                        </Badge>
                      )}
                      <span className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleDateString()}</span>
                      {c.video_url && (
                        <Badge variant="secondary" className="text-[10px] bg-cyan-500/15 text-cyan-300 border-cyan-500/30" title={c.image_url ? "A Sora 2 video clip is linked. Scheduled X posts will auto-attach it with your cover image as the thumbnail." : "A Sora 2 video clip is linked. Scheduled X posts will auto-attach it as native media."} data-testid={`video-attached-badge-${c.id}`}>
                          <Video className="w-3 h-3 mr-1 inline" />Video{c.image_url ? " + Thumb" : ""}
                        </Badge>
                      )}
                    </div>
                    <p className="font-medium truncate">{c.topic}</p>
                    <p className="text-sm text-muted-foreground line-clamp-2">{c.generated_content.substring(0, 150)}...</p>
                    {schedulingId === c.id ? (
                      <ScheduleControls
                        item={c}
                        linkedinConn={linkedinConn}
                        twitterConn={twitterConn}
                        onConfirm={(opts) => scheduleContent(c.id, opts)}
                        onCancel={() => setSchedulingId("")}
                      />
                    ) : (
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <Button size="sm" variant="ghost" className="text-xs" onClick={() => setViewingContent(c)} data-testid={`view-content-btn-${c.id}`}><Eye className="w-3 h-3 mr-1" />View Full</Button>
                        <Button size="sm" variant="ghost" className="text-xs" onClick={() => setSchedulingId(c.id)} data-testid={`schedule-btn-${c.id}`}><Calendar className="w-3 h-3 mr-1" />Schedule</Button>
                        <div className="flex items-center gap-1 ml-2 border-l border-white/10 pl-2">
                          <span className="text-xs text-muted-foreground mr-1">Share:</span>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-blue-400 hover:text-blue-300 hover:bg-blue-400/10" onClick={() => share.shareToTwitter(c)} title="Share to X"><Twitter className="w-3.5 h-3.5" /></Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-blue-600 hover:text-blue-500 hover:bg-blue-600/10" onClick={() => share.shareToLinkedIn(c)} title="Share to LinkedIn"><Linkedin className="w-3.5 h-3.5" /></Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-blue-500 hover:text-blue-400 hover:bg-blue-500/10" onClick={() => share.shareToFacebook(c)} title="Share to Facebook"><Facebook className="w-3.5 h-3.5" /></Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-green-500 hover:text-green-400 hover:bg-green-500/10" onClick={() => share.shareToWhatsApp(c)} title="Share to WhatsApp"><MessageCircle className="w-3.5 h-3.5" /></Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-pink-500 hover:text-pink-400 hover:bg-pink-500/10" onClick={() => share.copyForInstagram(c)} title="Copy for Instagram"><Instagram className="w-3.5 h-3.5" /></Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-cyan-400 hover:text-cyan-300 hover:bg-cyan-400/10" onClick={() => share.copyForTikTok(c)} title="Copy for TikTok"><Video className="w-3.5 h-3.5" /></Button>
                          <Button size="sm" variant="ghost" className={`h-7 w-7 p-0 ${c.demo_slug ? "text-teal-400 hover:text-teal-300 hover:bg-teal-400/10" : "text-amber-400 hover:text-amber-300 hover:bg-amber-400/10"}`} onClick={() => publishDemo(c)} title={c.demo_slug ? "Copy your public page link" : "Publish as a public page (earns you referral credit)"} data-testid={`publish-demo-btn-${c.id}`}><Globe className="w-3.5 h-3.5" /></Button>
                          {c.demo_slug && (
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground hover:bg-white/10" onClick={() => unpublishDemo(c)} title="Remove public page" data-testid={`unpublish-demo-btn-${c.id}`}><EyeOff className="w-3.5 h-3.5" /></Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => deleteContent(c.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Full Content View Modal */}
      <AnimatePresence>
        {viewingContent && (
          <ContentViewModal
            content={viewingContent}
            setContent={setViewingContent}
            onClose={() => setViewingContent(null)}
            refresh={fetchContent}
            share={share}
            user={user}
            token={token}
          />
        )}
      </AnimatePresence>

      {/* Limit-reached invite CTA — appears when free users exhaust their monthly quota */}
      <LimitReachedModal open={limitModalOpen} onClose={() => setLimitModalOpen(false)} />
    </div>
  );
}
